import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { StaffSelectionModalComponent } from '../staff-selection-modal/staff-selection-modal.component';

/**
 * Enhanced Staff Field Component
 *
 * Flexible component that handles both "Teacher in-charge" and "Teachers involved" fields.
 * Uses modal interface for better UX with search, filtering, and visual feedback.
 *
 * Features:
 * - Modal-based selection with search
 * - Chip preview of selected staff
 * - Initial letter avatars
 * - Cross-filtering support (exclude already selected staff)
 * - Read-only mode with pills display
 */
@Component({
  selector: 'app-enhanced-staff-field',
  standalone: false,
  templateUrl: './enhanced-staff-field.component.html',
  styleUrls: ['./enhanced-staff-field.component.scss'],
})
export class EnhancedStaffFieldComponent implements OnInit {

  @Input() fieldLabel: string = 'Staff';
  @Input() fieldType: 'incharge' | 'involved' = 'involved';
  @Input() pageSpecificFormObject: any;
  @Input() staffListMaster: any[] = [];
  @Input() excludedStaffIds: number[] = [];
  @Input() canUpdate: boolean = true;

  constructor(private modalController: ModalController) { }

  ngOnInit() {
    // Ensure arrays are initialized
    if (this.fieldType === 'incharge' && !this.pageSpecificFormObject.inchargeUserIds) {
      this.pageSpecificFormObject.inchargeUserIds = [];
    }
    if (this.fieldType === 'involved' && !this.pageSpecificFormObject.involvedUserIds) {
      this.pageSpecificFormObject.involvedUserIds = [];
    }
  }

  /**
   * Get selected staff IDs based on field type
   */
  get selectedStaffIds(): number[] {
    if (this.fieldType === 'incharge') {
      return this.pageSpecificFormObject?.inchargeUserIds || [];
    }
    return this.pageSpecificFormObject?.involvedUserIds || [];
  }

  /**
   * Set selected staff IDs based on field type
   */
  set selectedStaffIds(ids: number[]) {
    if (this.fieldType === 'incharge') {
      this.pageSpecificFormObject.inchargeUserIds = ids;
    } else {
      this.pageSpecificFormObject.involvedUserIds = ids;
    }
  }

  /**
   * Get available staff (excluding cross-filtered staff)
   */
  getAvailableStaff(): any[] {
    if (!this.staffListMaster || this.staffListMaster.length === 0) {
      return [];
    }

    // Filter out excluded staff IDs (cross-filtering)
    return this.staffListMaster.filter(staff => {
      const staffId = Number(staff.id);
      return !this.excludedStaffIds.some(excludedId => Number(excludedId) === staffId);
    });
  }

  /**
   * Get selected staff objects
   */
  getSelectedStaff(): any[] {
    const selectedIds = this.selectedStaffIds;
    if (!selectedIds || selectedIds.length === 0) {
      return [];
    }

    return this.staffListMaster
      .filter(staff => selectedIds.some(id => Number(id) === Number(staff.id)))
      .sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }

  /**
   * Get staff initials for avatar
   */
  getStaffInitials(staff: any): string {
    const first = staff.firstName?.charAt(0)?.toUpperCase() || '';
    const last = staff.lastName?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}`;
  }

  /**
   * Get full staff name
   */
  getStaffFullName(staff: any): string {
    return `${staff.firstName || ''} ${staff.lastName || ''}`.trim();
  }

  /**
   * Open selection modal
   */
  async openSelectionModal(): Promise<void> {
    const availableStaff = this.getAvailableStaff();

    const modal = await this.modalController.create({
      component: StaffSelectionModalComponent,
      componentProps: {
        allStaff: availableStaff,
        selectedStaffIds: this.selectedStaffIds,
        fieldLabel: this.fieldLabel
      },
      cssClass: 'staff-selection-modal',
      backdropDismiss: true
    });

    await modal.present();
    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm' && data) {
      this.selectedStaffIds = data.selectedIds;
    }
  }

  /**
   * Remove individual staff member
   */
  removeStaff(staffId: any): void {
    const idNumber = Number(staffId);
    // Create new array to trigger change detection
    this.selectedStaffIds = this.selectedStaffIds.filter(id => Number(id) !== idNumber);
  }

  /**
   * Clear all selected staff
   */
  clearAllStaff(): void {
    this.selectedStaffIds = [];
  }

  // Computed properties for template
  get hasStaff(): boolean {
    return this.getAvailableStaff().length > 0;
  }

  get hasSelections(): boolean {
    return this.selectedStaffIds.length > 0;
  }

  get selectedCount(): number {
    return this.selectedStaffIds.length;
  }
}
